import React, {useState} from "react";
import {ErrorMessage, Field, Form, Formik} from "formik";
import Select, {createFilter} from "react-select";
import Input from "../../../common/form-elements/input/input";
import FormErrorMessage from "../../../common/form-elements/form-error-message/form-error-message";
import AdvancedQueryBuilder from "../../../common/advanced-query-builder/advanced-query-builder";
import {
    maxLength,
    minLength,
    required,
    requiredSelect,
    schemaValidate,
} from "../../../../helpers/validation-helpers";
import {
    getFilterProperties,
    getColumnsData,
} from "../../../../pagingfeed/column-helpers/filterProperties";
import {DATA_TYPE_NAMES} from "../widget-constants";
import IconButton from "../../../common/icon-button/icon-button";
import ModalDefault from "../../../common/modal/modal-default";

const DATA_OPTIONS = [
    {value: DATA_TYPE_NAMES.AUDIT_POLICIES, label: "Audit policies"},
    {value: DATA_TYPE_NAMES.DEVICES, label: "Devices"},
    {value: DATA_TYPE_NAMES.FILES, label: "Files"},
    {value: DATA_TYPE_NAMES.OS_PLANS, label: "OS plans"},
    {value: DATA_TYPE_NAMES.SCHEDULES, label: "Schedules"},
];

const COUNT_VIEW_MODE_OPTIONS = [
    {value: "count", label: "Count"},
    {value: "gauge", label: "Gauge"},
];

const CountForm = ({
    initialValues,
    tempConfigData,
    onFormSubmit,
    onCancel,
    handleFormFieldChange,
    handleDataTypeChange,
    isLoadingColumnConfig,
}) => {
    const [showFilterConfig, setShowFilterConfig] = useState(false);

    const toggleFilterConfig = () => setShowFilterConfig((showFilterConfig) => !showFilterConfig);

    return (
        <Formik initialValues={initialValues} onSubmit={onFormSubmit}>
            {(props) => {
                return (
                    <Form>
                        <div className="glu-modal__body">
                            <div className="glu-content__section">
                                <div className="glu-form-row">
                                    <div className="glu-label">Widget Title</div>
                                    <Field
                                        as={Input}
                                        name="title"
                                        className="glu-form-input-pad"
                                        autoFocus
                                        value={props.values.title}
                                        onChange={handleFormFieldChange("title", props)}
                                        validate={schemaValidate([
                                            required(),
                                            minLength(),
                                            maxLength(30),
                                        ])}
                                        invalid={props.errors.title && props.touched.title}
                                    />
                                    <ErrorMessage name="title" component={FormErrorMessage} />
                                </div>

                                <div className="glu-form-row">
                                    <div className="glu-label">Data</div>
                                    <Field
                                        as={Select}
                                        name="dataType"
                                        value={props.values.dataType}
                                        options={DATA_OPTIONS}
                                        onChange={handleDataTypeChange("dataType", props)}
                                        validate={schemaValidate([requiredSelect().nullable()])}
                                        invalid={props.errors.dataType && props.touched.dataType}
                                        placeholder="Choose dataset"
                                        className="glu-select glu-form-input-pad"
                                        classNamePrefix="react-select"
                                        filterOption={createFilter({
                                            matchFrom: "any",
                                            stringify: (option) => `${option.label}`,
                                        })}
                                    />
                                    <ErrorMessage name="dataType" component={FormErrorMessage} />
                                </div>

                                <div className="glu-form-row">
                                    <div className="glu-label">Filter name</div>
                                    <Field
                                        as={Input}
                                        name="filterName"
                                        value={props.values.filterName}
                                        onChange={handleFormFieldChange("filterName", props)}
                                        placeholder="Enter filter name here..."
                                        validate={schemaValidate([maxLength(30)])}
                                        invalid={props.errors.title && props.touched.title}
                                        isDisabled={!props.values.filter.mongoQuery}
                                    />
                                    <ErrorMessage name="filterName" component={FormErrorMessage} />
                                </div>

                                <div className="glu-form-row">
                                    <div className="glu-label">View mode</div>
                                    <Field
                                        as={Select}
                                        name="viewMode"
                                        placeholder="Select view mode"
                                        value={props.values.viewMode}
                                        options={COUNT_VIEW_MODE_OPTIONS}
                                        onChange={handleFormFieldChange("viewMode", props)}
                                        handleFormFieldChange
                                        className="glu-select glu-form-input-pad"
                                        classNamePrefix="react-select"
                                        filterOption={createFilter({
                                            matchFrom: "any",
                                            stringify: (option) => `${option.label}`,
                                        })}
                                    />
                                </div>

                                <div className="glu-form-row">
                                    <div className="glu-flex-row glu-query-filter-icon">
                                        <IconButton
                                            icon="filter"
                                            text={
                                                Object.keys(props.values.filter.mongoQuery || {})
                                                    .length
                                                    ? "Configure filter"
                                                    : "Add filter"
                                            }
                                            onClick={toggleFilterConfig}
                                            className={
                                                Object.keys(props.values.filter.mongoQuery || {})
                                                    .length
                                                    ? "active"
                                                    : ""
                                            }
                                            isDisabled={
                                                !props.values.dataType ||
                                                !Object.keys(props.values.dataType).length ||
                                                !Object.keys(tempConfigData.columnsConfig).length
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <footer className="glu-modal__footer">
                            <div className="glu-flex-grow glu-flex-row">
                                <div className="glu-flex-grow text-right">
                                    <button
                                        onClick={onCancel}
                                        className="glu-btn glu-btn--secondary"
                                        type="button"
                                    >
                                        Cancel
                                    </button>
                                </div>
                                <div className="glu-flex-shrink">
                                    <button
                                        className="glu-btn glu-btn--primary"
                                        type="submit"
                                        disabled={isLoadingColumnConfig}
                                    >
                                        {isLoadingColumnConfig ? "Loading..." : "Save"}
                                    </button>
                                </div>
                            </div>
                        </footer>
                        <ModalDefault isOpen={showFilterConfig} customClass="glu-modal__filter">
                            <div className="glu-modal-content">
                                <div className="glu-modal__header">
                                    <div className="glu-modal__title glu-flex-grow">Filter</div>
                                    <div className="glu-flex-shrink">
                                        <button
                                            aria-label="close"
                                            type="button"
                                            className="glu-btn-link g-icon g-icon-close"
                                            onClick={toggleFilterConfig}
                                        />
                                    </div>
                                </div>
                                <div className="glu-modal__body">
                                    <div className="glu-content__section">
                                        <Field name="filter">
                                            {() => (
                                                <AdvancedQueryBuilder
                                                    saveFilter={handleFormFieldChange(
                                                        "filter",
                                                        props
                                                    )}
                                                    closeConfigModal={toggleFilterConfig}
                                                    tree={props.values.tree}
                                                    type={props.values.dataType}
                                                    columns={getColumnsData(
                                                        getFilterProperties(
                                                            props.values.dataType.value
                                                        ),
                                                        tempConfigData.columnsConfig
                                                    )}
                                                    filterData={tempConfigData.filter.mongoQuery}
                                                />
                                            )}
                                        </Field>
                                    </div>
                                </div>
                            </div>
                        </ModalDefault>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default CountForm;
